import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert
} from '@mui/material';
import { CognitoUser } from 'amazon-cognito-identity-js';
import { authService, AuthUser, MFASetupData, MFARequiredData } from '../services/auth';
import { MFASetup } from './MFASetup';
import { MFACodeInput } from './MFACodeInput';

interface LoginProps {
  onLogin: (email: string, token: string) => void;
}

type LoginState =
  | { type: 'LOGIN_FORM' }
  | { type: 'MFA_SETUP'; data: MFASetupData; cognitoUser: CognitoUser }
  | { type: 'MFA_CODE'; data: MFARequiredData; cognitoUser: CognitoUser };

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginState, setLoginState] = useState<LoginState>({ type: 'LOGIN_FORM' });

  const handleLoginSuccess = (user: AuthUser) => {
    localStorage.setItem('authToken', user.token);
    localStorage.setItem('authUser', user.email);
    onLogin(user.email, user.token);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authService.login(email, password);

      if (result.type === 'SUCCESS') {
        handleLoginSuccess(result.user);
      } else if (result.type === 'MFA_SETUP') {
        setLoginState({
          type: 'MFA_SETUP',
          data: result.data,
          cognitoUser: result.cognitoUser
        });
      } else if (result.type === 'MFA_REQUIRED') {
        setLoginState({
          type: 'MFA_CODE',
          data: result.data,
          cognitoUser: result.cognitoUser
        });
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setLoginState({ type: 'LOGIN_FORM' });
    setEmail('');
    setPassword('');
    setError('');
  };

  // Render MFA Setup screen
  if (loginState.type === 'MFA_SETUP') {
    return (
      <MFASetup
        mfaData={loginState.data}
        cognitoUser={loginState.cognitoUser}
        onSuccess={handleLoginSuccess}
        onCancel={handleCancel}
      />
    );
  }

  // Render MFA Code Input screen
  if (loginState.type === 'MFA_CODE') {
    return (
      <MFACodeInput
        mfaData={loginState.data}
        cognitoUser={loginState.cognitoUser}
        onSuccess={handleLoginSuccess}
        onCancel={handleCancel}
      />
    );
  }

  // Render Login Form
  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            CMS Roster Management
          </Typography>
          <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to manage users
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

